import { normalizeLocalDateTime, opaqueId } from './groupTripModel';
import { formatEffectiveJoinAt } from './groupTripLateJoinModel';
import {
  memberIdentityShortTag,
  TRAVEL_IDENTITY_GENERIC_COMPANION,
  travelMemberProductDisplayName,
} from '../member-identity/memberIdentityPresentation';

export const LIFECYCLE_COPY = {
  OPEN: '待處理',
  ASSESSING: '評估中',
  RESOLVABLE: '可結案',
  CLOSED: '已結案',
};

/** 與後端 DisruptionReason enum 一一對應。 */
export const REASON_COPY = {
  USER_DECLARED_PERSONAL: '旅伴個人因素',
  FAMILY_OR_PARTY_CHANGE: '家庭或同行小組異動',
  TRANSPORT_DISRUPTION: '交通異動',
  WEATHER: '天候因素',
  SUPPLIER_CHANGE: '供應商異動',
  SCHEDULE_CHANGE: '行程時間異動',
  OTHER: '其他',
};

export const REASON_OPTIONS = Object.keys(REASON_COPY);

/** 與後端 GroupTripBookingCategory enum 一一對應。 */
export const BOOKING_CATEGORY_COPY = {
  ACCOMMODATION: '住宿',
  TRANSPORT: '交通',
  ACTIVITY: '活動',
  RESTAURANT: '餐廳',
  OTHER: '其他訂位',
};

export const IMPACT_OPTIONS = [
  'UNKNOWN_PENDING_CONFIRMATION', 'NO_IMPACT', 'INDIVIDUAL_ONLY', 'PARTY_ONLY', 'GROUP_WIDE',
];

export const IMPACT_COPY = {
  UNKNOWN_PENDING_CONFIRMATION: '影響尚待確認',
  NO_IMPACT: '確認無影響',
  INDIVIDUAL_ONLY: '僅影響個別旅伴',
  PARTY_ONLY: '影響同行小組',
  GROUP_WIDE: '影響全團',
};

export const SUPPLIER_COPY = {
  UNKNOWN: '尚未向供應商確認',
  PENDING_SUPPLIER_CONFIRMATION: '等待供應商確認',
  CONFIRMED: '供應商已確認',
  UNAVAILABLE: '供應商無法確認（影響仍未確認）',
};

const copyOf = (table, fallback) => (value) => (
  Object.prototype.hasOwnProperty.call(table, value) ? table[value] : fallback
);

export const lifecycleLabel = copyOf(LIFECYCLE_COPY, '狀態未提供');
export const reasonLabel = copyOf(REASON_COPY, '其他異動原因');
export const impactLabel = copyOf(IMPACT_COPY, '影響狀態未提供');
export const supplierLabel = copyOf(SUPPLIER_COPY, '供應商狀態未提供');
export const bookingCategoryLabel = copyOf(BOOKING_CATEGORY_COPY, '訂位項目');

/** 後端 LocalDateTime 可能是 ISO 字串或 Jackson 陣列；不做時區換算。 */
export function formatDisruptionTime(value) {
  return formatEffectiveJoinAt(normalizeLocalDateTime(value)) || '時間未提供';
}

function referenceTag(prefix, id) {
  const tag = memberIdentityShortTag(id);
  return tag ? `${prefix} …${tag}` : '';
}

export function indexById(items, key) {
  const index = new Map();
  (Array.isArray(items) ? items : []).forEach((item) => {
    const id = opaqueId(item?.[key]);
    if (id) index.set(id, item);
  });
  return index;
}

export function travelerPresentation(travelerId, travelersById) {
  const traveler = travelersById?.get(opaqueId(travelerId));
  return {
    primary: travelMemberProductDisplayName(
      { displayName: traveler?.displayName },
      { genericFallback: TRAVEL_IDENTITY_GENERIC_COMPANION },
    ),
    reference: referenceTag('旅伴編號', travelerId),
  };
}

export function bookingPresentation(bookingId, bookingsById) {
  const booking = bookingsById?.get(opaqueId(bookingId));
  return {
    primary: bookingCategoryLabel(booking?.category),
    reference: referenceTag('訂位編號', bookingId),
  };
}

export function caseReference(caseId) {
  return referenceTag('案件編號', caseId);
}

export function normalizeDisruptionCase(value) {
  return {
    caseId: opaqueId(value?.caseId),
    groupTripId: opaqueId(value?.groupTripId),
    reason: value?.reason || 'OTHER',
    reasonDetail: value?.reasonDetail || '',
    lifecycle: value?.lifecycle || 'OPEN',
    rowVersion: Number(value?.rowVersion) || 0,
    affectedTravelerIds: Array.isArray(value?.affectedTravelerIds)
      ? value.affectedTravelerIds.map(opaqueId).filter(Boolean) : [],
    components: Array.isArray(value?.components)
      ? value.components.map(normalizeDisruptionComponent).filter((item) => item.componentId) : [],
    createTime: value?.createTime || null,
    updateTime: value?.updateTime || null,
  };
}

export function normalizeDisruptionComponent(value) {
  return {
    componentId: opaqueId(value?.componentId),
    bookingId: opaqueId(value?.bookingId),
    impactScope: value?.impactScope || 'UNKNOWN_PENDING_CONFIRMATION',
    affectedTravelerId: opaqueId(value?.affectedTravelerId),
    supplierConfirmationStatus: value?.supplierConfirmationStatus || 'UNKNOWN',
    evidenceReference: value?.evidenceReference || '',
    confirmedAt: value?.confirmedAt || null,
  };
}

export function disruptionError(cause) {
  const code = Number(cause?.code ?? cause?.raw?.code);
  const messages = {
    95130: '找不到此異動案件。',
    95131: '找不到此訂位影響項目。',
    95132: '你沒有操作此異動案件的權限。',
    95133: '目前案件狀態不允許這項操作，已重新載入最新資料。',
    95134: '資料已由其他人更新，已重新載入；請確認最新內容後再操作。',
    95135: '仍有未完成的影響評估，暫時不能結案。',
    95136: '案件已結案，不能再修改。',
    95137: '請提供 1 至 255 字的供應商確認依據。',
    95138: '這次請求與先前操作衝突，已重新載入最新資料。',
  };
  return { code, message: messages[code] || '目前無法完成操作，請稍後再試。' };
}

export function createDisruptionActionKey() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `disruption-${crypto.randomUUID()}`;
  }
  return `disruption-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function caseImpactSummary(item) {
  const unresolved = item.components.filter((component) => (
    component.impactScope === 'UNKNOWN_PENDING_CONFIRMATION'
    || component.supplierConfirmationStatus === 'PENDING_SUPPLIER_CONFIRMATION'
    || component.supplierConfirmationStatus === 'UNAVAILABLE'
  )).length;
  return unresolved ? `${unresolved} 項影響尚待確認` : `${item.components.length} 項影響已評估`;
}
